import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { View, Text, Image, FlatList } from 'react-native';
import moment from 'moment';
import { TouchableOpacity } from 'react-native-gesture-handler';
import { get as _get, slice as _slice } from 'lodash';
import { Icon as RNIcon } from 'react-native-elements';
import { useDispatch, useSelector } from 'react-redux';

import { trackScreenView } from '../../../lib/analytics';

import { RouteNames } from '../../../navigation/condoTypes';
import {
  fetchServiceRequest,
  selectIsLoading,
  selectServiceRequests,
  ServiceRequest,
  showModal,
} from '../../../store/appSlice';
import { ModalName } from '../../../components/RootOverlays';
import { RootState } from '../../../store/rootReducer';
import { RequestField } from '../components';
import styles from '../styles';
import { useIsFocused } from '@react-navigation/core';

type Props = {
  navigation: any;
  route: any;
};

const ServiceRequestScreen: React.FC<Props> = ({ navigation, route }) => {
  const dispatch = useDispatch();
  const [isShowMore, setShowMore] = useState(false);
  const isFocused = useIsFocused();
  const { contentId } = useMemo(() => {
    return {
      contentId: _get(route, 'params.contentId', ''),
    };
  }, [route]);
  console.log(`ServiceRequestScreen, contentId: ${contentId}`);

  const { messageId } = useMemo(() => {
    return {
      messageId: _get(route, 'params.messageId', ''),
    };
  }, [route]);
  console.log(`ServiceRequestScreen, messageId: ${messageId}`);

  const services = useSelector((state: RootState) =>
    selectServiceRequests(state),
  );
  const isLoading = useSelector((state: RootState) => selectIsLoading(state));

  const previewServices = useMemo(() => {
    return _slice(services, 0, 4);
  }, [services]);

  useEffect(() => {
    if (isFocused) {
      console.log(`loading service requests: ${messageId}`);
      dispatch(fetchServiceRequest());
    } else {
      console.log('not loading service requests (not focused)');
    }
  }, [dispatch, isFocused, messageId]);

  const onBackToPreview = () => setShowMore(false);

  const onViewAll = () => {
    setShowMore(true);
    trackScreenView('ViewAllServiceRequests');
  };

  const onShowDetail = useCallback(
    (item: ServiceRequest) => {
      const modalProps = {
        date: item.created_date
          ? moment(item.created_date).format('YYYY-MM-DD')
          : '',
        category: _get(item, 'category.name', ''),
        sub_category: _get(item, 'sub_category.name', ''),
        unit: _get(item, 'unit', ''),
        description: _get(item, 'description', ''),
        briefDescription: _get(item, 'brief_description', ''),
        status: _get(item, 'status.name', ''),
        allowSuiteAccess: _get(item, 'allow_suite_access', '') === 'true',
        visible: true,
        isLoading,
      };
      dispatch(
        showModal({
          modalName: ModalName.ServiceRequestDetail,
          modalProps,
          isShowModal: true,
        }),
      );
    },
    [dispatch, isLoading],
  );

  useEffect(() => {
    if (contentId && messageId && services) {
      const serviceItem = services.find(
        (service) => String(service.id) === contentId,
      );
      if (serviceItem) {
        console.log(
          `useEffect - onShowDetail: contentId:${contentId}, messageId:${messageId}`,
        );
        onShowDetail(serviceItem);
      }
    }
  }, [contentId, services, onShowDetail, messageId]);

  const onShowCreate = useCallback(() => {
    navigation.navigate(RouteNames.CreateServiceRequest);
  }, [navigation]);

  const onGoBack = useCallback(() => {
    navigation.goBack();
  }, [navigation]);

  return (
    <>
      <FlatList
        ListHeaderComponent={
          <>
            {!isShowMore ? (
              <RNIcon
                tvParallaxProperties={null}
                onPress={onGoBack}
                iconStyle={styles.topBack}
                containerStyle={styles.iconButtonContainer}
                name="arrow-back"
              />
            ) : (
              <RNIcon
                tvParallaxProperties={null}
                onPress={onBackToPreview}
                iconStyle={styles.topBack}
                name="arrow-back"
              />
            )}
            <Image
              style={styles.headerImage}
              resizeMode="contain"
              source={require('../../../assets/cogwheel.png')}
            />
            {!isShowMore && (
              <Text style={styles.titleFlatList}>Recent Service Requests</Text>
            )}
          </>
        }
        style={styles.container}
        nestedScrollEnabled
        keyExtractor={(item: any) => `${item.id}`}
        renderItem={({ item }: { item: ServiceRequest }) => (
          <RequestField item={item} onPress={onShowDetail} />
        )}
        data={isShowMore ? services : previewServices}
        ListFooterComponent={
          !isShowMore ? (
            <>
              {(services || []).length >= 4 && (
                <TouchableOpacity onPress={onViewAll}>
                  <Text style={styles.viewAll}>View all</Text>
                </TouchableOpacity>
              )}
              <TouchableOpacity onPress={onShowCreate}>
                <View style={styles.circleIcon}>
                  <Image
                    style={styles.addImage}
                    source={require('../../../assets/ic_plus.png')}
                  />
                </View>
                <Text style={styles.bottomTitle}>Create a new request</Text>
              </TouchableOpacity>
            </>
          ) : null
        }
      />
    </>
  );
};

export default ServiceRequestScreen;
